import {
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {useTranslation} from "react-i18next"
import { GenericImage } from "../../../Shared/components/GenericImage";
import { CompanyForm } from "./CompanyForm";
import { OverlayLoader } from "../../../Shared/components/Overlay";
import { useQuery } from "@tanstack/react-query";
import { ToastResponse } from "../../../Shared/types/ToastProps";
import { Toast } from "../../../Shared/components/Toast";
import { UtilService } from "../../../Shared/Utils/UtilService";
import { CompanyService } from "../services/company.service";
import { GenericZoomFile } from "../../../Shared/components/GenericZoomFile";
import { ProfileForm } from "./ProfileForm";
import { ApprovalForm } from "./ApprovalForm";
import { GenericButton } from "../../../Shared/components/GenericButton";
import { AuthService } from "../../Auth/services/AuthService";

export const Company: React.FC = () => {
  const navigate = useNavigate();
  const {t} = useTranslation()
  const [searchParams] = useSearchParams();
  const company = searchParams.get("company");
  const message = t('loading_message');
  let isAllowed = AuthService.isManagementAllowed();
  const [isLoading, setIsLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toast, setToast] = useState<ToastResponse>();
  const [account,setAccount] = useState<any>()
  const [companyData,setCompanyData] = useState<any>()

  const findCompany = async () => {
    try {
      setIsLoading(true);
      const res =  await CompanyService.getById(company ?? "");
      console.log("🚀 ~ findCompany ~ res:", res)
      setCompanyData(res)
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const findAccount = async () => {
    try {
      setIsLoading(true);
      const res =  await CompanyService.getAccount(company ?? "");
      setAccount(res)
    } catch (error: any) {
      setIsLoading(false);
      setShowToast(true);
      setToast({ message: `Error ${error?.message}`, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  // const query = useQuery({
  //   queryKey: ["company"],
  //   queryFn: findCompanyAccount,
  //   placeholderData: (previous) => previous?.data,
  // });

  // const queryAccount = useQuery({
  //   queryKey: ["account"],
  //   queryFn: findAccount,
  //   placeholderData: (previous) => previous?.data,
  // });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const onAddAction = () => setIsModalOpen(!isModalOpen);
  const toggleModal = (success: boolean) => {
    setIsModalOpen(!isModalOpen);
    // if (success) {
    //   query.refetch();
    // }
  };

  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const onAddApprovalAction = () =>
    setIsApprovalModalOpen(!isApprovalModalOpen);
  const toggleApprovalModal = (success: boolean) => {
    setIsApprovalModalOpen(!isApprovalModalOpen);
    // if (success) {
    //   query.refetch();
    // }
  };

  const onViewAllAction = () => {
    navigate("/posts/company", { state: { company: `${company}` } });
  };

  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const onPostAction = () => setIsPostModalOpen(!isPostModalOpen);
  const togglePostModal = (success: boolean) => {
    setIsPostModalOpen(!isPostModalOpen);
    // if (success) {
    //   query.refetch();
    // }
  };

  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const onAddProfileAction = () => setIsProfileModalOpen(!isProfileModalOpen);
  const toggleProfileModal = (success: boolean) => {
    setIsProfileModalOpen(!isProfileModalOpen);
    // if (success) {
    //   query.refetch();
    // }
  };

  const isAuthorized =
    companyData?.approvals?.filter((item: any) => item.isAuthorized === true)
      ?.length > 0;
  const hasApprovalRequest = companyData?.company?.approvals?.length > 0;

  const onRequestApproval = async () => {
    onAddApprovalAction();
  };

  const loadData = async()=>{
    await Promise.all([findCompany(),findAccount()])
  }

  useEffect(()=>{
    loadData()
  },[])
  return (
    <>
      {isModalOpen && (
        <CompanyForm onClose={toggleModal} company={companyData} />
      )}

      {isProfileModalOpen && (
        <ProfileForm
          onClose={toggleProfileModal}
          profile={companyData?.profile}
          company={companyData}
        />
      )}
      
      {isApprovalModalOpen && (
        <ApprovalForm onClose={toggleApprovalModal} company={companyData!} />
      )}

      <section className="antialiased dark:bg-gray-900">
        <div className="mx-auto max-w-screen-lg px-4 2xl:px-0">
          <div className="py-4 md:py-8">
            <div className="mb-4 grid gap-4 sm:grid-cols-2 sm:gap-8 lg:gap-16">
              <div className="space-y-4">
                <div className="flex space-x-4">
                  <div className="p-3 bg-gray-400 rounded">
                    {companyData?.profile ? (
                      <GenericZoomFile
                        src={companyData?.profile?.contactToAttachments?.[0]}
                      />
                    ) : (
                      <GenericImage />
                    )}
                  </div>
                  <div>
                    <span className="mb-2 inline-block rounded bg-primary-100 px-2.5 py-0.5 text-xs font-medium text-primary-800 dark:bg-primary-900 dark:text-primary-300">
                      {t('member_since')}{" "}
                      {UtilService.dateFormat(companyData?.createdAt) ||
                        ""}
                    </span>
                    <h2 className="flex items-center text-xl font-bold leading-none text-gray-900 dark:text-white sm:text-2xl">
                      {UtilService.titleCase(companyData?.name ?? "")}
                    </h2>
                  </div>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                   {t('email_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.email}
                  </dd>
                </div>

                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('phone_number_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.phone}
                  </dd>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('registration_date')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.registrationDate}
                  </dd>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('address')}
                  </div>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4" />
                    {companyData?.address || "Add Address"}
                  </dd>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('about')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.description}
                  </dd>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex row">
                  {!isAllowed && (
                    <GenericButton
                      isFull={false}
                      type="button"
                      className="py-2 bg-blue-700"
                      onClick={onAddProfileAction}
                    >
                      {companyData?.profile ? t('update_profile') : t('add_profile')}
                    </GenericButton>
                  )}

                  <div className="">&nbsp;&nbsp;&nbsp;</div>
                  {isAuthorized ? (
                    <div className="p-2 bxg-green-500 rounded-md text-green-800">
                      {t('approved')}
                    </div>
                  ) : (
                    <div className="">
                      {hasApprovalRequest && (
                        <div className="py-2 px-3 bg-yellow-300 rounded-md text-dark-600">
                          {t('pending_approval')}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="">&nbsp;&nbsp;</div>
                  {!isAllowed && account?.requestApproval && (
                    <div className="">
                      {!isAuthorized ? (
                        <GenericButton
                          isFull={false}
                          type="button"
                          className="py-2 bg-gray-500"
                          onClick={() => onRequestApproval()}
                        >
                          {t('request_approval')}
                        </GenericButton>
                      ) : (
                        ""
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('tax_payer_id_label')}
                  </div>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    {companyData?.taxPayerId ? (
                      <GenericZoomFile src={companyData?.taxPayerId} />
                    ) : (
                      t('tax_payer_id_hint')
                    )}
                  </dd>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('license_label')}
                  </div>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    {companyData?.license ? (
                      <GenericZoomFile src={companyData?.license} />
                    ) : (
                      t('license_label')
                    )}
                  </dd>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('other_attachments_label')}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    {companyData?.attachments?.length ? (
                      <ol
                        type="1"
                        className="list-disc list-inside flex flex-row gap-4"
                      >
                        {companyData?.attachments.map(
                          (attachment: any, index: number) => (
                            <li key={index} className="flex items-center">
                              {attachment ? (
                                <GenericZoomFile src={attachment} />
                              ) : (
                                ""
                              )}
                            </li>
                          )
                        )}
                      </ol>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          (window.location.href =
                            "/services/accounts/companies")
                        }
                        className="text-primary-700 hover:underline"
                      >
                        {t('add_company')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {!isAllowed && (
              <button
                type="button"
                onClick={onAddAction}
                className="inline-flex w-full items-center justify-center rounded-lg bg-primary-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-primary-800 focus:outline-none focus:ring-4 focus:ring-primary-300 dark:bg-primary-600 dark:hover:bg-primary-700 dark:focus:ring-primary-800 sm:w-auto"
              >
                <PencilSquareIcon className="h-4 w-4 mr-2" />
                {t('edit_account')}
              </button>
            )}
            {/* <div className="hidden">
              &nbsp; &nbsp;
              <button
                type="button"
                onClick={onPostAction}
                className="inline-flex w-full items-center justify-center rounded-lg bg-white-700 px-5 py-2.5 text-sm font-medium text-gray-800 hover:bg-white-800 focus:outline-none focus:ring-4 focus:ring-white-300 dark:bg-white-600 dark:hover:bg-white-700 dark:focus:ring-white-800 sm:w-auto"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Post
              </button>
              &nbsp;&nbsp;
              <button
                type="button"
                onClick={onViewAllAction}
                className="inline-flex w-full items-center justify-center rounded-lg bg-white-700 px-5 py-2.5 text-sm font-medium text-gray-800 hover:bg-white-800 focus:outline-none focus:ring-4 focus:ring-white-300 dark:bg-white-600 dark:hover:bg-white-700 dark:focus:ring-white-800 sm:w-auto"
              >
                {" "}
                <ViewfinderCircleIcon className="h-4 w-4 mr-2" />
                All Posts
              </button>
            </div> */}
          </div>

          <div className="bg-info max-width">
            <div className="mb-4 grid gap-4 sm:grid-cols-2 sm:gap-8 lg:gap-16">
              <div className="space-y-4">
                <div className="font-bold text-xl">
                  {t('contact_person_in')}{companyData?.country}
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('full_name_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactFromFullName}
                  </dd>
                </div>

                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('phone_number_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactFromPhone}
                  </dd>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                   {t('email_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactFromEmail}
                  </dd>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('address')}
                  </div>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4" />
                    {companyData?.contactFromAddress || t('address')}
                  </dd>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('attachments')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactFromAttachments?.length ? (
                      <ol
                        type="1"
                        className="list-disc list-inside flex flex-row gap-4"
                      >
                        {companyData?.profile?.contactFromAttachments.map(
                          (attachment: any, index: number) => (
                            <li key={index} className="flex items-center">
                              {attachment && (
                                <GenericZoomFile
                                  src={attachment}
                                  height={100}
                                  width={100}
                                />
                              )}
                            </li>
                          )
                        )}
                      </ol>
                    ) : (
                      t('add_attachment')
                    )}
                  </dd>
                </div>
              </div>
              <div className="space-y-4">
                <div className="font-bold text-xl">{t('contact_info_drc')}</div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('full_name_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactToFullName}
                  </dd>
                </div>

                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('phone_number_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactToPhone}
                  </dd>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('email_label')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactToEmail}
                  </dd>
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('address')}
                  </div>
                  <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                    <MapPinIcon className="h-4 w-4" />
                    {companyData?.profile?.contactToAddress || t('address')}
                  </dd>
                </div>
                <div className="">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {t('attachments')}
                  </div>
                  <dd className="text-gray-500 dark:text-gray-400">
                    {companyData?.profile?.contactToAttachments?.length ? (
                      <ol
                        type="1"
                        className="list-disc list-inside flex flex-row gap-4"
                      >
                        {companyData?.profile?.contactToAttachments.map(
                          (attachment: any, index: number) => (
                            <li key={index} className="flex items-center">
                              {attachment && (
                                <GenericZoomFile
                                  src={attachment}
                                  height={100}
                                  width={100}
                                />
                              )}
                            </li>
                          )
                        )}
                      </ol>
                    ) : (
                      t('add_attachment')
                    )}
                  </dd>
                </div>
              </div>
            </div>
          </div>

          {/* <div className="grid grid-cols-2 gap-6 border-b border-t border-gray-200 py-4 dark:border-gray-700 md:py-8 lg:grid-cols-4 xl:gap-16">
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">{t('users')}</h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                {account?.users}
                <span className="ms-2 inline-flex items-center rounded bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  10.3%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 20 last 3 months
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">
                Vehicles
              </h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                {account?.vehicles}
                <span className="ms-2 inline-flex items-center rounded bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  8.6%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 14 last 3 months
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">Drivers</h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                {account?.drivers}
                <span className="ms-2 inline-flex items-center rounded bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900 dark:text-red-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  12%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 10 last 3 months
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-gray-500 dark:text-gray-400">
                Attachments
              </h3>
              <span className="flex items-center text-2xl font-bold text-gray-900 dark:text-white">
                {account?.attachments}
                <span className="ms-2 inline-flex items-center rounded bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900 dark:text-green-300">
                  <svg
                    className="-ms-1 me-1 h-4 w-4"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v13m0-13 4 4m-4-4-4 4"
                    ></path>
                  </svg>
                  50%
                </span>
              </span>
              <p className="mt-2 flex items-center text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                <svg
                  className="me-1.5 h-4 w-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
                vs 1 last 3 months
              </p>
            </div>
          </div> */}
        </div>
      </section>
      {isLoading && <OverlayLoader message={message} />}
      {showToast && (
        <Toast
          type={toast?.type}
          message={toast?.message ?? ""}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};
